import { MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import React, { useCallback, useMemo } from 'react';
import type { FilterOperator } from '../types';

type FilterOperatorSelectProps = {
  operator: string;
  operators: FilterOperator[];
  disabled: boolean;
  onOperatorChange: (value: string) => void;
};

const ArrowDownIcon = () => <VyIcon icon="ArrowDown" />;

export const FilterOperatorSelect: React.FC<FilterOperatorSelectProps> = React.memo(
  ({ operator, operators, disabled, onOperatorChange }) => {
    const isSelectDisabled = useMemo(() => {
      return disabled || operators.length <= 1;
    }, [disabled, operators.length]);
    const handleChange = useCallback(
      (event: SelectChangeEvent<string>) => {
        onOperatorChange(event.target.value);
      },
      [onOperatorChange],
    );

    const selectedOperatorLabel = useMemo(() => {
      const selectedOperator = operators.find((op) => op.value === operator);
      return selectedOperator?.label || '';
    }, [operators, operator]);

    return (
      <Select
        {...getSelectorAttribute(vyTableSelectors.filterOperatorSelect)}
        size="small"
        value={operator || ''}
        onChange={handleChange}
        disabled={isSelectDisabled}
        displayEmpty
        IconComponent={isSelectDisabled ? () => null : ArrowDownIcon}
        renderValue={(value) => (
          <span style={{ color: value ? 'inherit' : '#999' }}>
            {value ? selectedOperatorLabel : 'Select...'}
          </span>
        )}
        sx={{
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          width: '170px',
          height: '36px',
          fontSize: '14px',
          '&:hover:not(.Mui-disabled)': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused': {
            borderColor: '#1976d2',
            borderWidth: '2px',
            boxShadow: 'none',
          },
          '& .MuiSelect-select': {
            padding: '8px 12px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      >
        {operators.map((operator) => (
          <MenuItem key={operator.value} value={operator.value}>
            {operator.label}
          </MenuItem>
        ))}
      </Select>
    );
  },
);
